import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from 'rebass/styled-components';

import CreatorFileOverlayIcons from 'features/media/components/molecules/CreatorFileOverlayIcons';
import { useCollections } from 'features/media/useCollections';
import { useFiles } from 'features/media/useFiles';
import { useSubmissionFileNotification } from 'features/submissions/hooks/useSubmissionFileNotification';
import { useSubmissions } from 'features/submissions/useSubmissions';
import { Box, Flex } from 'shared/components/display';
import CollectionExplorer from 'shared/components/molecules/CollectionExplorer';
import DragAndDropUpload from 'shared/components/molecules/DragAndDropUpload/DragAndDropUpload';
import FileExplorer from 'shared/components/molecules/FileExplorer';
import MediaUploadButton from 'shared/components/molecules/MediaUploadButton';
import Modal from 'shared/components/molecules/Modal';

import { FILE_COPY, GENERIC_COPY } from 'shared/config/copy';
import { SIZING } from 'shared/config/formatting';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { useDeepEffect } from 'shared/hooks/useDeepEffect';
import { useQuery } from 'shared/hooks/useQuery';
import { useSearchParams } from 'shared/hooks/useSearchParams';
import { intl } from 'shared/lib/intl';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import { ModuleFileType } from 'shared/typings/briefs';
import { BrkfstFile } from 'shared/typings/file';

import {
  CollectionValidator,
  MultiValidator,
  PlatformValidator,
  SubmissionValidator,
} from 'shared/utilities/validator';
import { getPlatformFormatsByFileType } from 'shared/utilities/validator/utils';

import { footerStyles, styles } from './styles';
import { copyFiles, formatUploadOperations } from './utils';

const { TEXT_ONLY } = BUTTON_VARIANTS;

interface Props {
  /** Callback invoked when files are selected from the user's collection via the modal file explorer */
  getSelectedFiles: (files: BrkfstFile[]) => void;
  /** Callback invoked when files are directly uploaded to this submission module (not from a collection) */
  getDirectlyUploadedFiles: (files: BrkfstFile[]) => void;
  fileType: ModuleFileType;
  isSubmission: boolean;
  minAssetDuration: number;
  maxAssetDuration: number;
  disabled: boolean;
  moduleNum: number;
  removedFileIds: number[];
  allSubmittedFiles: Pick<BrkfstFile, 'id' | 'name' | 'metadata' | 'submissionValidatorFields'>[];
  moduleFiles: Pick<BrkfstFile, 'id' | 'name' | 'metadata' | 'submissionValidatorFields'>[];
}

const SubmissionAssetUpload: React.FC<Props> = ({
  getSelectedFiles,
  getDirectlyUploadedFiles,
  fileType,
  minAssetDuration,
  maxAssetDuration,
  disabled,
  moduleNum,
  allSubmittedFiles,
  removedFileIds,
  moduleFiles,
}) => {
  const { getQuery } = useQuery();
  const query = getQuery();
  const { submissionId } = useParams<{ submissionId: string }>();
  const { submission } = useSubmissions({ submissionId: +submissionId });
  const { currentUser } = useCurrentUser();

  const { collections, defaultCollection, getFilteredCollections } = useCollections();
  const [modalOpen, setModalOpen] = useState(false);

  const {
    collectionFiles,
    selectedFiles,
    changeSelectedCollection,
    initiateMultipleFilesUpload,
    selectedCollectionId,
    selectFiles,
    filesUploading,
  } = useFiles();

  const collectionParams = useSearchParams(query, {
    collectionPage: 1,
    collectionSize: 10,
    orderBy: 'dateCreated',
    orderAsc: true,
    type: '',
    userCreated: currentUser.id,
  });

  const numSelectedFiles = selectedFiles.length;
  const submittedNotRemovedFiles = useMemo(() => {
    return allSubmittedFiles.filter(({ id }) => !removedFileIds.includes(id));
  }, [allSubmittedFiles, removedFileIds]);

  const fileNamesAlreadyInModule = useMemo(() => {
    return new Set(moduleFiles.map((file) => file.name));
  }, [moduleFiles]);

  const toggleModal = () => {
    selectFiles([]);
    setModalOpen((prev) => !prev);
  };

  const clearFileState = () => {
    selectFiles([]);
  };

  const onModalClose = () => {
    clearFileState();
    toggleModal();
  };

  // Submits files selected from collection
  const submitSelected = async () => {
    if (selectedFiles.length) {
      const allFileNames = new Set([...fileNamesAlreadyInModule, ...filesUploading.map(({ name }) => name)]);
      const filesCopied = await copyFiles({
        files: selectedFiles,
        baseFileName,
        fileType,
        fileNamesAlreadyInModule: allFileNames,
      });

      // Add moduleNum to register into redux state
      getSelectedFiles(Object.values(filesCopied.map((file) => ({ ...file, moduleNum: moduleNum.toString() }))));
    }

    // close modal
    setModalOpen(false);

    clearFileState();
  };

  /**
   * Formats the prefix of the submission file name
   * @example <CreatorInitials><CreatorId>_B<briefId>_M<ModuleNumber>
   **/
  const baseFileName = useMemo(() => {
    const creatorInitials = `${currentUser.firstName[0]}${currentUser.lastName[0]}`;
    return `B${submission?.briefId}_${creatorInitials}${currentUser.id}_M${moduleNum}`;
  }, [currentUser.id, submission?.briefId, moduleNum, currentUser.firstName, currentUser.lastName]);

  const uploadFilesToCollection = useCallback(
    async (files) => {
      if (files.length) {
        const collectionId = selectedCollectionId || defaultCollection.id;
        if (!selectedCollectionId) changeSelectedCollection(collectionId);
        const assets = await formatUploadOperations({
          files,
          collectionId,
          validatorFields: { minAssetDuration, maxAssetDuration, moduleNum, fileType },
          fileNamesAlreadyInModule,
        });
        const collection = collections.find(({ id }) => id === collectionId) || defaultCollection;
        await initiateMultipleFilesUpload(
          assets,
          new MultiValidator(
            new CollectionValidator(collection.id, collectionFiles, collection.numFiles),
            new SubmissionValidator(minAssetDuration, maxAssetDuration, moduleNum, fileType),
            new PlatformValidator(fileType),
          ),
        );
      }
    },
    [
      selectedCollectionId,
      defaultCollection.id,
      collectionFiles,
      minAssetDuration,
      maxAssetDuration,
      collections,
      moduleNum,
      fileType,
      fileNamesAlreadyInModule,
      defaultCollection,
      changeSelectedCollection,
      initiateMultipleFilesUpload,
    ],
  );

  const uploadFilesToSubmission = useCallback(
    async (files) => {
      const assets = await formatUploadOperations({
        files,
        fileName: baseFileName,
        submissionId: +submissionId,
        validatorFields: { minAssetDuration, maxAssetDuration, moduleNum, fileType },
        fileNamesAlreadyInModule,
      });
      await initiateMultipleFilesUpload(
        assets,
        new MultiValidator(
          new SubmissionValidator(minAssetDuration, maxAssetDuration, moduleNum, fileType, submittedNotRemovedFiles),
          new PlatformValidator(fileType),
        ),
      );
    },
    [
      submittedNotRemovedFiles,
      moduleNum,
      submissionId,
      minAssetDuration,
      maxAssetDuration,
      fileType,
      initiateMultipleFilesUpload,
      baseFileName,
      fileNamesAlreadyInModule,
    ],
  );

  const acceptedMedia = getPlatformFormatsByFileType(fileType);

  const onDirectUpload = (file: BrkfstFile) => {
    // Only for files uploaded to the specific module and are not already in a collection
    if (file.submissionValidatorFields?.moduleNum === moduleNum && (file.collectionIds?.length || 0) < 1) {
      getDirectlyUploadedFiles([file]);
    }
  };

  useSubmissionFileNotification({
    onFileUploaded: onDirectUpload,
  });

  useDeepEffect(() => {
    const { collectionSize, collectionPage, ...filter } = collectionParams;
    getFilteredCollections({
      ...filter,
      page: collectionPage,
      size: collectionSize,
    });
  }, [collectionParams]);

  const formatGalleryItems = (asset) => {
    // submitted to this submission and not removed during the editing process before saving
    const submitted = Boolean(submittedNotRemovedFiles.find(({ id }) => id === asset.id));
    let selectDisabled = (asset.submitted || submitted) && !removedFileIds.includes(asset.id);
    let selectTooltip = submitted
      ? FILE_COPY.TOOLTIP_FILE_ALREADY_SUBMITTED
      : FILE_COPY.TOOLTIP_FILE_ALREADY_IN_ANOTHER_SUBMISSION;

    return {
      itemObject: asset,
      OverlayLeft: <CreatorFileOverlayIcons file={asset} showTags showSubmitted={submitted} />,
      captionData: asset,
      selectDisabled,
      selectTooltip: selectDisabled ? selectTooltip : '',
    };
  };

  return (
    <Box className="submission-asset-upload" data-cy={`submission-asset-upload--module-${moduleNum}`}>
      <Box onClick={disabled ? undefined : toggleModal}>
        <DragAndDropUpload
          onChange={uploadFilesToSubmission}
          supportedMedia={fileType}
          acceptedMedia={acceptedMedia}
          disabled={disabled}
          noClick
          multiple
        />
      </Box>
      {modalOpen && (
        <Modal
          isOpen={modalOpen}
          onRequestClose={onModalClose}
          title={intl.formatMessage(
            {
              id: 'HEADING_SELECT_ASSET',
            },
            {
              media: fileType,
            },
          )}
          component={
            <>
              {!selectedCollectionId ? (
                <CollectionExplorer
                  header={
                    <Flex css={styles} className="submission-asset-upload__upload-btn">
                      <MediaUploadButton
                        acceptedMedia={acceptedMedia}
                        buttonContent={'Select files from your device'}
                        multipleFiles
                        onFileUpload={(files) => {
                          toggleModal();
                          uploadFilesToSubmission(files);
                        }}
                      />
                    </Flex>
                  }
                  collectionParams={collectionParams}
                  isCreatorCollection
                />
              ) : (
                <FileExplorer
                  collectionId={selectedCollectionId}
                  uploadNewFiles={uploadFilesToCollection}
                  fileType={fileType}
                  acceptedMedia={acceptedMedia}
                  clearFileState={clearFileState}
                  formatGalleryItems={formatGalleryItems}
                />
              )}
            </>
          }
          footer={
            <Flex css={footerStyles} className="submission-asset-upload">
              <Button variant={TEXT_ONLY} onClick={onModalClose} className="submission-asset-upload__cancel-btn">
                {GENERIC_COPY.BUTTON_CANCEL}
              </Button>
              <Button
                data-cy="file-explorer__submit"
                onClick={submitSelected}
                className="submission-asset-upload__submit-btn"
                disabled={disabled}
              >
                {intl.formatMessage(
                  {
                    id: 'BUTTON_PICK_ASSET',
                  },
                  {
                    numFiles: numSelectedFiles,
                    media: fileType,
                  },
                )}
              </Button>
            </Flex>
          }
          modalSize={SIZING.LARGE}
        />
      )}
    </Box>
  );
};

export default SubmissionAssetUpload;
